import { ReactNode, KeyboardEvent, useRef, useCallback } from 'react';
import styles from './CardRadioGroup.module.css';

export interface CardRadioOption<T> {
  /** הערך הייחודי */
  value: T;
  /** כותרת לתצוגה */
  label: string;
  /** תיאור משני (קצר) */
  description?: string;
  /** אייקון (אופציונלי) */
  icon?: ReactNode;
  /** "ברירת מחדל" / "מומלץ" וכו' */
  badge?: string;
  /** מנטרל ספציפית את האופציה */
  disabled?: boolean;
}

export type CardRadioGroupLayout = 'horizontal' | 'vertical' | 'grid';

export interface CardRadioGroupProps<T> {
  /** מערך אופציות (2-6 מומלץ) */
  options: CardRadioOption<T>[];
  /** הערך הנבחר */
  value: T;
  onChange: (v: T) => void;
  /** horizontal=row, vertical=column, grid=responsive */
  layout?: CardRadioGroupLayout;
  /** חובה: תיאור הקבוצה ל-screen readers */
  ariaLabel: string;
  required?: boolean;
  /** מנטרל את כל הקבוצה */
  disabled?: boolean;
  className?: string;
}

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

/**
 * בחירת אופציה אחת מ-2-6 אופציות, עם תצוגה גדולה כ-cards (R4 — לא pills).
 *
 * **משמש ב-AddExpenseForm** (R4) ובכל מקום שדורש בחירה visual.
 * ARIA pattern: roving tabIndex — רק האופציה הנבחרת ב-Tab order; arrows מנווטים.
 *
 * @example
 * <CardRadioGroup
 *   options={[
 *     { value: 'service', label: 'חודש שירות' },
 *     { value: 'reception', label: 'חודש קבלה' },
 *   ]}
 *   value={mode}
 *   onChange={setMode}
 *   ariaLabel="סוג חודש"
 * />
 */
export function CardRadioGroup<T>({
  options,
  value,
  onChange,
  layout = 'horizontal',
  ariaLabel,
  required = false,
  disabled = false,
  className,
}: CardRadioGroupProps<T>): JSX.Element {
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectedIndex = options.findIndex((opt) => opt.value === value);

  const moveFocusTo = useCallback(
    (newIndex: number): void => {
      if (newIndex < 0 || newIndex >= options.length) return;
      const opt = options[newIndex];
      if (opt.disabled || disabled) return;
      onChange(opt.value);
      const el = optionRefs.current[newIndex];
      if (el) el.focus();
    },
    [options, onChange, disabled]
  );

  const findNextEnabled = (start: number, direction: 1 | -1): number => {
    let i = start + direction;
    while (i >= 0 && i < options.length) {
      if (!options[i].disabled) return i;
      i += direction;
    }
    return -1;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number): void => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        e.preventDefault();
        const next = findNextEnabled(idx, 1);
        if (next !== -1) moveFocusTo(next);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        e.preventDefault();
        const prev = findNextEnabled(idx, -1);
        if (prev !== -1) moveFocusTo(prev);
        break;
      }
      case ' ':
      case 'Enter':
        e.preventDefault();
        if (!options[idx].disabled) onChange(options[idx].value);
        break;
      case 'Home': {
        e.preventDefault();
        const first = options.findIndex((o) => !o.disabled);
        if (first !== -1) moveFocusTo(first);
        break;
      }
      case 'End': {
        e.preventDefault();
        for (let i = options.length - 1; i >= 0; i -= 1) {
          if (!options[i].disabled) {
            moveFocusTo(i);
            break;
          }
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div
      className={cx(styles.root, styles[layout], disabled && styles.disabled, className)}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-required={required || undefined}
      aria-disabled={disabled || undefined}
      data-testid="card-radio-group"
    >
      {options.map((opt, idx) => {
        const isSelected = opt.value === value;
        const isDisabled = disabled || opt.disabled;
        // Roving tabindex — האופציה הנבחרת ב-Tab order. אם אין selected, הראשונה הזמינה.
        const isInTabOrder =
          (selectedIndex === -1 && idx === options.findIndex((o) => !o.disabled)) ||
          isSelected;

        return (
          <button
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            ref={(el: HTMLButtonElement | null) => {
              optionRefs.current[idx] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-disabled={isDisabled || undefined}
            tabIndex={isInTabOrder && !isDisabled ? 0 : -1}
            disabled={isDisabled}
            className={cx(
              styles.card,
              isSelected && styles.selected,
              isDisabled && styles.cardDisabled
            )}
            onClick={() => !isDisabled && onChange(opt.value)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            data-testid={`card-radio-option-${idx}`}
          >
            {opt.icon !== undefined && opt.icon !== null && (
              <span className={styles.icon} aria-hidden="true">
                {opt.icon}
              </span>
            )}
            <span className={styles.body}>
              <span className={styles.label}>{opt.label}</span>
              {opt.description && (
                <span className={styles.description}>{opt.description}</span>
              )}
            </span>
            {opt.badge && (
              <span className={styles.badge} data-testid={`card-radio-badge-${idx}`}>
                {opt.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
